// src/components/tutor/mobile/AnnouncementDetailPage.js
//
// Standalone deep-link page for push notification routing.
// Reached via /announcement?id=<announcementId> (React Router, query param).
// Independently fetches its own data — no dependency on the feed list,
// no props, no race conditions with async subscriptions.
//
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiUser,
  FiClock,
  FiSend,
  FiPaperclip,
} from "react-icons/fi";
import { BsFillPinAngleFill } from "react-icons/bs";
import {
  collection,
  doc,
  addDoc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";
import "./MobileAnnouncements.css";

// ============================================================================
// LOGIC  (all self-contained — no imports from TutorAnnouncementsLogic)
// ============================================================================

async function fetchAnnouncement(id) {
  const snap = await getDoc(doc(db, "announcements", id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

async function markAsRead(announcementId) {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  const user = auth.currentUser;
  const snap = await getDoc(doc(db, "users", uid));
  const data = snap.exists() ? snap.data() : {};
  await setDoc(
    doc(db, "announcements", announcementId, "reads", uid),
    {
      uid,
      userName: data.name || user.displayName || "User",
      userEmail: data.email || user.email || "",
      userRole: data.role || "tutor",
      readAt: serverTimestamp(),
      method: "deep-link",
    },
    { merge: true }
  );
}

function subscribeAttachments(announcementId, onRows) {
  const q = query(
    collection(db, "announcements", announcementId, "attachments"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snap) =>
    onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
}

function subscribeComments(announcementId, onRows) {
  const q = query(
    collection(db, "announcements", announcementId, "comments"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snap) =>
    onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
}

async function postComment(announcementId, text) {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  const user = auth.currentUser;
  const snap = await getDoc(doc(db, "users", uid));
  const data = snap.exists() ? snap.data() : {};
  const clean = (text || "").replace(/​/g, "").replace(/ /g, " ").trim();
  if (!clean) return;
  await addDoc(
    collection(db, "announcements", announcementId, "comments"),
    {
      uid,
      userName: data.name || user.displayName || "User",
      userEmail: data.email || user.email || "",
      userRole: data.role || "tutor",
      text: clean,
      parentId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(ts) {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  return (
    d.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    }) +
    " at " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function CommentsSection({ announcementId, commentsEnabled }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!announcementId) return;
    return subscribeComments(announcementId, setComments);
  }, [announcementId]);

  const handlePost = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      await postComment(announcementId, text);
      setText("");
    } catch (err) {
      console.error("Failed to post comment:", err);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="man-comments">
      <div className="man-comments-title">Comments</div>

      {comments.length === 0 && (
        <div className="man-comments-empty">No comments yet.</div>
      )}

      <div className="man-comments-list">
        {comments.map((c) => (
          <div key={c.id} className="man-comment-item">
            <div className="man-comment-top">
              <span className="man-comment-author">{c.userName || "User"}</span>
              <span className="man-comment-time">
                {c.createdAt?.toDate
                  ? c.createdAt
                      .toDate()
                      .toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                  : ""}
              </span>
            </div>
            <div className="man-comment-text">{c.text}</div>
          </div>
        ))}
      </div>

      {commentsEnabled ? (
        <div className="man-comment-input-row">
          <input
            className="man-comment-input"
            placeholder="Add a comment..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handlePost();
              }
            }}
          />
          <button
            className="man-comment-send"
            onClick={handlePost}
            disabled={!text.trim() || posting}
          >
            <FiSend size={14} />
          </button>
        </div>
      ) : (
        <div className="man-comments-disabled">Comments are disabled.</div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AnnouncementDetailPage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id") || "";
  const navigate = useNavigate();

  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [attachments, setAttachments] = useState([]);

  // Swipe-to-back refs
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // 1. Fetch the announcement directly by ID — single targeted read, no feed list needed
  useEffect(() => {
    if (!id) { setLoading(false); setNotFound(true); return; }
    setLoading(true);
    fetchAnnouncement(id)
      .then((a) => {
        if (!a) setNotFound(true);
        else setAnnouncement(a);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  // 1b. Clear the Cache API notification intent for this announcement.
  //     Without this, navigating back to "/" re-mounts TutorDashboard, which
  //     re-reads the old Cache API entry and tries to re-open the same
  //     announcement — causing an unwanted loop.
  useEffect(() => {
    if (!id || !window.caches) return;
    caches.open('notification-intents-v1')
      .then((cache) => cache.match('/notification-intent')
        .then((res) => {
          if (!res) return;
          res.json().then((data) => {
            if (data?.announcementId === id) cache.delete('/notification-intent');
          }).catch(() => {});
        })
      ).catch(() => {});
  }, [id]);

  // 2. Mark as read the moment announcement data arrives — fire and forget
  useEffect(() => {
    if (!announcement?.id) return;
    markAsRead(announcement.id).catch(console.error);
  }, [announcement?.id]);

  // 3. Subscribe to attachments (real-time, in case admin adds one)
  useEffect(() => {
    if (!id) return;
    return subscribeAttachments(id, setAttachments);
  }, [id]);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="man-detail">
        <div className="man-detail-header">
          <button className="man-back-btn" onClick={() => navigate("/")}>
            <FiArrowLeft size={22} />
            <span>Announcements</span>
          </button>
        </div>
        <div className="man-detail-scroll">
          <div className="adp-loading">Loading announcement…</div>
        </div>
      </div>
    );
  }

  // ── Not found state ────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <div className="man-detail">
        <div className="man-detail-header">
          <button className="man-back-btn" onClick={() => navigate("/")}>
            <FiArrowLeft size={22} />
            <span>Announcements</span>
          </button>
        </div>
        <div className="man-detail-scroll">
          <div className="adp-not-found">
            <div className="adp-not-found-title">Announcement not found</div>
            <div className="adp-not-found-sub">
              It may have been deleted or you may not have access.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Announcement detail ────────────────────────────────────────────────────
  const commentsEnabled = !(
    announcement?.allowComments === false ||
    announcement?.allowComments === "false" ||
    announcement?.commentsEnabled === false ||
    announcement?.commentsEnabled === "false"
  );

  const author =
    announcement.createdByName || announcement.authorName || "Admin";
  const dateStr = formatDate(announcement.createdAt);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (dx > 80 && dx > dy * 1.5) navigate("/");
    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <div
      className="man-detail"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Sticky header */}
      <div className="man-detail-header">
        <button className="man-back-btn" onClick={() => navigate("/")}>
          <FiArrowLeft size={22} />
          <span>Announcements</span>
        </button>
      </div>

      {/* Scrollable content */}
      <div className="man-detail-scroll">
        {/* Pinned badge */}
        {announcement.pinned && (
          <div className="man-detail-pinned">
            <BsFillPinAngleFill size={11} />
            <span>Pinned</span>
          </div>
        )}

        {/* Title */}
        <h1 className="man-detail-title">{announcement.title}</h1>

        {/* Meta */}
        <div className="man-detail-meta">
          <FiUser size={13} />
          <span>{author}</span>
          <span className="man-meta-sep">·</span>
          <FiClock size={13} />
          <span>{dateStr}</span>
        </div>

        {/* Divider */}
        <div className="man-detail-divider" />

        {/* Body HTML */}
        <div
          className="man-detail-body"
          dangerouslySetInnerHTML={{ __html: announcement.contentHtml || "" }}
        />

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="man-detail-attachments">
            <div className="man-attachments-label">Attachments</div>
            {attachments.map((a) => (
              <a
                key={a.id}
                href={a.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="man-attach-link"
              >
                <FiPaperclip size={13} />
                <span>{a.fileName}</span>
              </a>
            ))}
          </div>
        )}

        {/* Comments */}
        <CommentsSection
          announcementId={id}
          commentsEnabled={commentsEnabled}
        />
      </div>
    </div>
  );
}
